'use client';
import { IRepositoryBranch } from '@/lib/repositoryBranchesApi';
import { ICommit, IEntityUser } from '@/types';
import * as Icon from '@/ui/Icon';

import { Row, Stack } from '@/ui/primatives';
import * as Styled from './styled';
import { toTimeAgo } from '@/lib/dateTime';
import { UserAvatar } from '@/components/Avatar';
import { CheckIcon } from '@radix-ui/react-icons';

const BranchOption: React.FC<{ branch: IRepositoryBranch; isSelected: boolean }> = ({ branch, isSelected }) => (
  <Styled.SelectOption>
    <CheckIcon height="20" width="20" style={{ opacity: isSelected ? 1 : 0 }} />
    <Stack gap={5}>
      <Row gap={10} alignItems="center">
        <Icon.Branch size="12" />
        <div className="commitRef">{branch?.name || 'huh?'}</div>
      </Row>
      <CommitInfo commit={branch.commit} />
    </Stack>
  </Styled.SelectOption>
);

const CommitInfo: React.FC<{ commit: ICommit }> = ({ commit }) => {
  const user: IEntityUser = {
    id: commit.author,
    username: commit.author,
    name: commit.author,
    email: commit.email,
    image: undefined,
  };
  return (
    <Styled.CommitInfo>
      <UserAvatar user={user} size={12} />
      <span>{commit.author}</span>・<span>{toTimeAgo(commit.timestamp)}</span>
    </Styled.CommitInfo>
  );
};

const CommitOption: React.FC<{ commit: ICommit; isSelected: boolean }> = ({ commit, isSelected }) => (
  <Styled.SelectOption>
    <CheckIcon height="20" width="20" style={{ opacity: isSelected ? 1 : 0 }} />
    <Stack gap={10}>
      <Row gap={10} alignItems="center">
        <Icon.Commit size="12px" />
        <div className="commitRef">{commit?.id || 'huh?'}</div>
      </Row>
      <CommitInfo commit={commit} />
    </Stack>
  </Styled.SelectOption>
);

type SingleValueBranchProps = { branch: IRepositoryBranch; commit?: never };

type SingleValueCommitProps = { commit: ICommit; branch?: never };

const MergedSingleValue: React.FC<SingleValueBranchProps | SingleValueCommitProps> = ({ commit, branch }) => (
  <Styled.SingleValue>
    <Row gap={10} alignItems="center">
      {commit ? <Icon.Commit size="12" /> : <Icon.Branch size="12" />}
      <div className="commitRef">{commit?.id || branch?.name || 'huh?'}</div>
    </Row>
  </Styled.SingleValue>
);

const Option = (props: any) => {
  const { innerProps, isDisabled, data, getStyles, isSelected } = props;
  const branch: IRepositoryBranch = data.branch;
  const commit: ICommit = data.commit;
  return !isDisabled ? (
    <div data-component="CustomOption" style={getStyles('option', props)} {...innerProps}>
      {branch ? (
        <BranchOption branch={branch} isSelected={isSelected} />
      ) : (
        <CommitOption commit={commit} isSelected={isSelected} />
      )}
    </div>
  ) : null;
};

const SingleValue = (props: any) => {
  const { innerProps, isDisabled, data, getStyles } = props;
  const branch: IRepositoryBranch = data.branch;
  const commit: ICommit = data.commit;

  return !isDisabled ? (
    <div data-component="CustomValue" style={getStyles('option', props)} {...innerProps}>
      {branch ? <MergedSingleValue branch={branch} /> : <MergedSingleValue commit={commit} />}
    </div>
  ) : null;
};

const Placeholder = (props: any) => {
  const { innerProps, selectProps, getStyles } = props;
  return (
    <div data-component="Placeholder" style={getStyles('option', props)} {...innerProps}>
      <Styled.SingleValue>
        <Row gap={10} alignItems="center">
          <Icon.Branch size="12" />
          <span className="commitRef" style={{ fontWeight: 400 }}>
            {selectProps.placeholder}
          </span>
        </Row>
      </Styled.SingleValue>
    </div>
  );
};

export default { Option, SingleValue, Placeholder };
